import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Package, ExternalLink, Star } from "lucide-react";
import { toast } from "sonner";

export default function SoluzioneDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [proj, setProj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("dettaglio"); // 'dettaglio' | 'wizard'
  const [config, setConfig] = useState({
    dimensione_azienda: "piccola",
    https_automatico: "si",
    backup_giornaliero: "si",
    lingua_interfaccia: "italiano",
    note_aggiuntive: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/oss/projects/${slug}`).then((r) => setProj(r.data)).catch(() => toast.error("Progetto non trovato")).finally(() => setLoading(false));
  }, [slug]);

  const generatePackage = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/oss/deployments", {
        project_slug: slug,
        config,
      });
      toast.success("Generazione pacchetto avviata");
      navigate(`/soluzioni/deployment/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="adr-skeleton h-96" />;
  if (!proj) return null;

  return (
    <div className="max-w-3xl">
      <Link to="/soluzioni" className="inline-flex items-center gap-1.5 text-sm text-[#555555] hover:text-[#0F3D6B] mb-6">
        <ArrowLeft className="w-4 h-4" /> Torna al catalogo
      </Link>

      <div className="flex items-start gap-4 mb-6">
        <div className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(15,61,107,0.08)" }}>
          <Package className="w-7 h-7" style={{ color: "#0F3D6B" }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="adr-badge" style={{ background: "rgba(15,61,107,0.08)", color: "#0F3D6B" }}>{proj.license}</span>
            <span className="adr-badge capitalize" style={{ background: "rgba(85,85,85,0.1)", color: "#555555" }}>{proj.difficulty}</span>
            {proj.avg_rating && <span className="adr-badge" style={{ background: "rgba(234,179,8,0.15)", color: "#a16207" }}><Star className="w-3 h-3 inline" /> {proj.avg_rating}/5 · {proj.feedback_count}</span>}
          </div>
          <h1 className="serif text-3xl font-semibold tracking-tight text-[#1A1A1A] leading-tight mb-2">{proj.name}</h1>
          <p className="text-[#555555]">{proj.short_description_it}</p>
          <a href={proj.repo_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#0F3D6B] hover:underline mt-2">
            Repository ufficiale <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {step === "dettaglio" && (
        <>
          {proj.use_cases_pmi?.length > 0 && (
            <div className="adr-card p-6 mb-4">
              <div className="adr-overline mb-3">Casi d'uso PMI manifatturiera</div>
              <ul className="space-y-2 text-sm text-[#1A1A1A]">
                {proj.use_cases_pmi.map((u, i) => <li key={i} className="flex gap-2"><span className="text-[#0F3D6B] mt-0.5">•</span> {u}</li>)}
              </ul>
            </div>
          )}
          <div className="adr-card p-6 mb-4">
            <div className="adr-overline mb-3">Risorse minime consigliate</div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><div className="text-xs text-[#555555]">CPU</div><div className="serif text-lg">{proj.min_resources?.cpu || 2} core</div></div>
              <div><div className="text-xs text-[#555555]">RAM</div><div className="serif text-lg">{proj.min_resources?.ram_gb || 4} GB</div></div>
              <div><div className="text-xs text-[#555555]">Disco</div><div className="serif text-lg">{proj.min_resources?.disk_gb || 20} GB</div></div>
            </div>
          </div>
          <button onClick={() => setStep("wizard")} className="adr-btn-primary text-base" data-testid="configura-button">
            Configura per la mia azienda →
          </button>
        </>
      )}

      {step === "wizard" && (
        <div className="adr-card p-6">
          <div className="adr-overline mb-4">Configurazione guidata</div>
          <h2 className="serif text-xl font-semibold mb-4">Aiutaci a personalizzare l'installazione</h2>
          <div className="space-y-5">
            <div>
              <label className="adr-label">Dimensione della tua azienda</label>
              <select className="adr-input" value={config.dimensione_azienda} onChange={(e) => setConfig({ ...config, dimensione_azienda: e.target.value })} data-testid="wiz-dimensione">
                <option value="micro">Micro (≤ 10 addetti)</option>
                <option value="piccola">Piccola (11-50 addetti)</option>
                <option value="media">Media (51-250 addetti)</option>
                <option value="grande">Grande (&gt; 250 addetti)</option>
              </select>
            </div>
            <div>
              <label className="adr-label">Vuoi HTTPS automatico (Traefik + Let's Encrypt)?</label>
              <select className="adr-input" value={config.https_automatico} onChange={(e) => setConfig({ ...config, https_automatico: e.target.value })}>
                <option value="si">Sì, configura HTTPS automatico</option>
                <option value="no">No, gestisco io il reverse proxy</option>
              </select>
            </div>
            <div>
              <label className="adr-label">Backup giornaliero dei volumi?</label>
              <select className="adr-input" value={config.backup_giornaliero} onChange={(e) => setConfig({ ...config, backup_giornaliero: e.target.value })}>
                <option value="si">Sì, includi script di backup</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <label className="adr-label">Lingua interfaccia (se supportata)</label>
              <select className="adr-input" value={config.lingua_interfaccia} onChange={(e) => setConfig({ ...config, lingua_interfaccia: e.target.value })}>
                <option value="italiano">Italiano</option>
                <option value="inglese">Inglese</option>
              </select>
            </div>
            <div>
              <label className="adr-label">Note aggiuntive (opzionale)</label>
              <textarea className="adr-input min-h-[80px]" value={config.note_aggiuntive} onChange={(e) => setConfig({ ...config, note_aggiuntive: e.target.value })} placeholder="Es. integrare con il nostro Active Directory, esposizione su porta 8080..." />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setStep("dettaglio")} className="adr-btn-outline">Indietro</button>
              <button onClick={generatePackage} disabled={submitting} className="adr-btn-primary" data-testid="generate-package">
                {submitting ? "Generazione..." : "Genera il pacchetto"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
